/* ================================================================
   Input masks – pure functions, no external dependencies.
   Each mask returns the formatted string from raw user input.
   Each unmask strips formatting characters back to raw digits/text.
   ================================================================ */

/** CPF: 000.000.000-00 */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function unmaskCpf(value: string): string {
  return value.replace(/\D/g, '');
}

/** Brazil mobile: (00) 00000-0000 — keeps 10 digits looking incomplete. */
export function maskBrazilianMobile(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return digits.replace(/(\d{0,2})/, '($1');
  if (digits.length <= 7) return digits.replace(/(\d{2})(\d{0,5})/, '($1) $2');
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
}

/** Phone: (00) 00000-0000  or  (00) 0000-0000 (Brazil default) */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return digits.replace(/(\d{0,2})/, '($1');
  if (digits.length <= 6) return digits.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (digits.length <= 10)
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
}

export function unmaskPhone(value: string): string {
  return value.replace(/\D/g, '');
}

/** Country-aware phone mask based on calling code */
export function maskPhoneByCountry(
  value: string,
  callingCode: string,
  options?: { preferMobile?: boolean },
): string {
  const code = callingCode.split('_')[0];
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  switch (code) {
    case '55': { // Brazil: (11) 99999-9999 or (11) 9999-9999
      if (options?.preferMobile) {
        return maskBrazilianMobile(digits);
      }

      const d = digits.slice(0, 11);
      if (d.length <= 2) return d.replace(/(\d{0,2})/, '($1');
      if (d.length <= 6) return d.replace(/(\d{2})(\d{0,4})/, '($1) $2');
      if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
      return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
    case '1': { // US/Canada: (555) 123-4567
      const d = digits.slice(0, 10);
      if (d.length <= 3) return d.replace(/(\d{0,3})/, '($1');
      if (d.length <= 6) return d.replace(/(\d{3})(\d{0,3})/, '($1) $2');
      return d.replace(/(\d{3})(\d{3})(\d{0,4})/, '($1) $2-$3');
    }
    case '351': { // Portugal: 912 345 678
      const d = digits.slice(0, 9);
      if (d.length <= 3) return d;
      if (d.length <= 6) return d.replace(/(\d{3})(\d{0,3})/, '$1 $2');
      return d.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1 $2 $3');
    }
    case '54': { // Argentina: 11 1234-5678
      const d = digits.slice(0, 10);
      if (d.length <= 2) return d;
      if (d.length <= 6) return d.replace(/(\d{2})(\d{0,4})/, '$1 $2');
      return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '$1 $2-$3');
    }
    case '44': { // UK: 7911 123456
      const d = digits.slice(0, 10);
      if (d.length <= 4) return d;
      return d.replace(/(\d{4})(\d{0,6})/, '$1 $2');
    }
    case '49': { // Germany: 1512 3456789
      const d = digits.slice(0, 11);
      if (d.length <= 4) return d;
      return d.replace(/(\d{4})(\d{0,7})/, '$1 $2');
    }
    case '33': { // France: 6 12 34 56 78
      const d = digits.slice(0, 9);
      if (d.length <= 1) return d;
      return d.replace(/(\d)(\d{0,2})(\d{0,2})(\d{0,2})(\d{0,2})/, (_m, a, b, c, dd, e) =>
        [a, b, c, dd, e].filter(Boolean).join(' ')
      );
    }
    case '39': { // Italy: 312 345 6789
      const d = digits.slice(0, 10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return d.replace(/(\d{3})(\d{0,3})/, '$1 $2');
      return d.replace(/(\d{3})(\d{3})(\d{0,4})/, '$1 $2 $3');
    }
    case '34': { // Spain: 612 345 678
      const d = digits.slice(0, 9);
      if (d.length <= 3) return d;
      if (d.length <= 6) return d.replace(/(\d{3})(\d{0,3})/, '$1 $2');
      return d.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1 $2 $3');
    }
    default: { // Generic: groups of 4
      const d = digits.slice(0, 15);
      return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
  }
}

/** Expected national number length (digits only), aligned with maskPhoneByCountry. */
export function getPhoneDigitLengthRange(callingCode: string): { min: number; max: number } {
  switch (callingCode.split('_')[0]) {
    case '55': return { min: 10, max: 11 };
    case '1': return { min: 10, max: 10 };
    case '351': return { min: 9, max: 9 };
    case '54': return { min: 10, max: 10 };
    case '44': return { min: 10, max: 10 };
    case '49': return { min: 10, max: 11 };
    case '33': return { min: 9, max: 9 };
    case '39': return { min: 9, max: 10 };
    case '34': return { min: 9, max: 9 };
    default: return { min: 6, max: 15 };
  }
}

/** Max length for phone input by calling code */
export function phoneMaxLength(callingCode: string): number {
  switch (callingCode.split('_')[0]) {
    case '55': return 15;  // (11) 99999-9999
    case '1': return 14;   // (555) 123-4567
    case '351': return 11; // 912 345 678
    case '54': return 12;  // 11 1234-5678
    case '44': return 11;  // 7911 123456
    case '49': return 12;  // 1512 3456789
    case '33': return 13;  // 6 12 34 56 78
    case '39': return 12;  // 312 345 6789
    case '34': return 11;  // 612 345 678
    default: return 19;    // generic
  }
}

/** Placeholder for phone input by calling code */
export function phonePlaceholder(callingCode: string): string {
  switch (callingCode.split('_')[0]) {
    case '55': return '(11) 99999-9999';
    case '1': return '(555) 123-4567';
    case '351': return '912 345 678';
    case '54': return '11 1234-5678';
    case '44': return '7911 123456';
    case '49': return '1512 3456789';
    case '33': return '6 12 34 56 78';
    case '39': return '312 345 6789';
    case '34': return '612 345 678';
    default: return '1234 5678';
  }
}

/** CEP: 00000-000 (Brazil default) */
export function maskCep(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d{1,3})/, '$1-$2');
}

export function unmaskCep(value: string): string {
  return value.replace(/\D/g, '');
}

/** Country-aware postal code unmask: strips only formatting, keeps letters for countries that use them */
export function unmaskPostalCode(value: string, countryCode: string): string {
  switch (countryCode) {
    case 'BR':
    case 'US':
    case 'PT':
      return value.replace(/\D/g, '');
    case 'CA':
    case 'GB':
    default:
      return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  }
}

/** Country-aware postal code mask */
export function maskPostalCode(value: string, countryCode: string): string {
  switch (countryCode) {
    case 'BR': {
      const digits = value.replace(/\D/g, '').slice(0, 8);
      return digits.replace(/(\d{5})(\d{1,3})/, '$1-$2');
    }
    case 'US': {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      if (digits.length <= 5) return digits;
      return digits.replace(/(\d{5})(\d{1,4})/, '$1-$2');
    }
    case 'PT': {
      const digits = value.replace(/\D/g, '').slice(0, 7);
      return digits.replace(/(\d{4})(\d{1,3})/, '$1-$2');
    }
    case 'CA': {
      const clean = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
      if (clean.length <= 3) return clean;
      return clean.slice(0, 3) + ' ' + clean.slice(3);
    }
    case 'GB': {
      return value.toUpperCase().slice(0, 8);
    }
    default: {
      return value.replace(/[^A-Za-z0-9\s\-]/g, '').slice(0, 10);
    }
  }
}

/** Max length for postal code input by country */
export function postalCodeMaxLength(countryCode: string): number {
  switch (countryCode) {
    case 'BR': return 9;   // 00000-000
    case 'US': return 10;  // 00000-0000
    case 'PT': return 8;   // 0000-000
    case 'CA': return 7;   // A0A 0A0
    case 'GB': return 8;   // SW1A 1AA
    default: return 10;
  }
}

/** Placeholder for postal code input by country */
export function postalCodePlaceholder(countryCode: string): string {
  switch (countryCode) {
    case 'BR': return '00000-000';
    case 'US': return '00000';
    case 'PT': return '0000-000';
    case 'CA': return 'A0A 0A0';
    case 'GB': return 'SW1A 1AA';
    default: return 'Código postal';
  }
}

const formatMetersDisplay = (meters: number): string => (
  meters.toFixed(2).replace('.', ',')
);

const formatKgDisplay = (kg: number): string => (
  kg.toFixed(2).replace('.', ',')
);

/** Height in meters: 1,75. Digits 175 become 1,75. */
export function maskHeight(value: string): string {
  const hasSeparator = /[.,]/.test(value);
  const digits = value.replace(/\D/g, '').slice(0, 3);
  if (!digits) {
    return '';
  }
  if (digits.length === 1) {
    return hasSeparator ? `${digits},` : digits;
  }
  if (digits.length === 2) {
    return `${digits[0]},${digits[1]}`;
  }
  return `${digits[0]},${digits.slice(1)}`;
}

export function unmaskHeight(value: string): string {
  return value.replace(',', '.');
}

export function parseHeightMeters(value: string | number): number | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return value > 3 ? value / 100 : value;
  }

  const raw = unmaskHeight(value.trim());
  if (!raw || raw === '.') {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed > 3 ? parsed / 100 : parsed;
}

export function formatHeightMeters(value: string | number): string {
  if (typeof value === 'string' && !value.trim()) {
    return '';
  }

  const meters = parseHeightMeters(value);
  if (meters == null) {
    return typeof value === 'string' ? maskHeight(value) : '';
  }

  return formatMetersDisplay(meters);
}

/** Height in centimeters: integers only (max 3 digits) */
export function maskHeightCm(value: string): string {
  return value.replace(/\D/g, '').slice(0, 3);
}

/** Weight: last two digits are decimals (6045 → 60,45), max 300,00 */
export function maskWeight(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 5);
  if (!digits) {
    return '';
  }

  const cents = Math.min(Number(digits), 30000);
  const padded = String(cents).padStart(3, '0');
  const integerPart = Number(padded.slice(0, -2));
  const decimalPart = padded.slice(-2);
  return `${integerPart},${decimalPart}`;
}

export function unmaskWeight(value: string): string {
  return value.replace(',', '.');
}

export function parseWeightKg(value: string | number): number | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return Math.min(value, 300);
  }

  const raw = unmaskWeight(value.trim());
  if (!raw || raw === '.') {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.min(parsed, 300);
}

export function formatWeightKg(value: string | number): string {
  if (typeof value === 'string' && !value.trim()) {
    return '';
  }

  const kg = parseWeightKg(typeof value === 'string' ? maskWeight(value) : value);
  if (kg == null) {
    return typeof value === 'string' ? maskWeight(value) : '';
  }

  return formatKgDisplay(kg);
}

/** Instagram: ensures @ prefix */
export function maskInstagram(value: string): string {
  const cleaned = value.replace(/^@+/, '').replace(/\s/g, '');
  if (!cleaned) return '';
  return '@' + cleaned;
}

export function unmaskInstagram(value: string): string {
  return value.replace(/^@/, '');
}

/** Date: dd/mm/aaaa */
export function maskDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isValidCalendarDate(day: number, month: number, year: number): boolean {
  if (year < 1900 || year > 2100) {
    return false;
  }

  if (month < 1 || month > 12) {
    return false;
  }

  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day;
}

/** Converts DD/MM/YYYY to YYYY-MM-DD when valid. */
export function parseMaskedDateToIso(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) {
    return null;
  }

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));

  if (!isValidCalendarDate(day, month, year)) {
    return null;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Converts YYYY-MM-DD to DD/MM/YYYY for display. */
export function formatIsoDateToMasked(isoDate: string): string {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return '';
  }

  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

export function isValidIsoDate(isoDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return false;
  }

  const [year, month, day] = isoDate.split('-').map(Number);
  return isValidCalendarDate(day, month, year);
}

export function isValidMaskedDate(value: string): boolean {
  return parseMaskedDateToIso(value) !== null;
}

/** Credit card number: 0000 0000 0000 0000 */
export function maskCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

export function unmaskCardNumber(value: string): string {
  return value.replace(/\D/g, '');
}

/** Credit card expiry: MM/AA */
export function maskCardExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);

  if (digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export function maskCardCvv(value: string): string {
  return value.replace(/\D/g, '').slice(0, 4);
}
